import { useEffect, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useApi } from "../hooks/useApi";

import LoadingComponent from "../shared_modules/loadingComponent";

const BarberScheduleDialog = ({
  showBarberScheduleDialog,
  setShowBarberScheduleDialog,
  setShowBarberScheduleAlert,
  selectedBarber,
  onSave,
}) => {
  const api = useApi();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [barberName, setBarberName] = useState(null);
  const [schedule, setSchedule] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  useEffect(() => {
    if (selectedBarber) {
      retrieveBarberSchedule();
    }
  }, [selectedBarber]);


  const handleClose = (event, reason) => {
    if (reason !== "backdropClick") {
      showBarberScheduleDialog({ show: false, schedule: schedule });
    }
  };

  const retrieveBarberSchedule = async () => {
    setIsLoading(true);

    try {
      const response = await api(
        `/barbers/schedule.php?barber_id=${selectedBarber}`,
        { method: "GET" },
      );
      setBarberName(response.data.barber_name);
      setSchedule(response.data.hours);
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const saveBarberSchedule = async () => {
    setIsSubmitting(true);
    setShowAlert({ show: false, message: "", type: "" });

    try {
      const response = await api(
        `/barbers/schedule.php?barber_id=${selectedBarber}`,
        {
          method: "PUT",
          body: JSON.stringify({ hours: schedule }),
        },
      );

      setShowBarberScheduleAlert({
        show: true,
        message: "Successfully updated schedule.",
        type: "success",
      });
      setShowBarberScheduleDialog({
        show: false,
        schedule: response.data.barber_id,
      });
      onSave();
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDay = (index) => {
    const updated = [...schedule];
    updated[index] = {
      ...updated[index],
      is_working: updated[index].is_working === 1 ? 0 : 1,
    };
    setSchedule(updated);
  };

  const changeOpenTime = (openTime, index) => {
    const updated = [...schedule];
    updated[index] = {
      ...updated[index],
      start_time: openTime,
    };
    setSchedule(updated);
  };

  const changeCloseTime = (closeTime, index) => {
    const updated = [...schedule];
    updated[index] = {
      ...updated[index],
      end_time: closeTime,
    };
    setSchedule(updated);
  };

  const renderSchedule = () => {
    return (
      <Box>
        {schedule.map((day, index) => (
          <Box
            key={day.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              opacity: day.is_working ? "1" : "0.6",
              backgroundColor: day.is_working ? "inherit" : "grey.200",
              borderBottom:
                index !== schedule.length - 1 ? `0.1px solid` : undefined,
              borderColor: "customDivider",
              p: 2,
            }}
          >
            {/* Toggle + day name row */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={day.is_working === 1}
                  disabled={isSubmitting}
                  onChange={() => toggleDay(index)}
                />
                <Typography variant="subtitle1">{day.day_name}</Typography>
              </Box>

              {/* Desktop: time fields inline */}
              {day.is_working ? (
                <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1 }}>
                  <TextField
                    variant="outlined"
                    placeholder="Open Time"
                    type="time"
                    value={day.start_time}
                    disabled={isSubmitting}
                    onChange={(e) => changeOpenTime(e.target.value, index)}
                    sx={{ width: 135, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                  <Typography variant="body1">to</Typography>
                  <TextField
                    variant="outlined"
                    placeholder="Close Time"
                    type="time"
                    value={day.end_time}
                    disabled={isSubmitting}
                    onChange={(e) => changeCloseTime(e.target.value, index)}
                    sx={{ width: 135, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                  Not Working
                </Typography>
              )}
            </Box>

            {/* Mobile: time fields on their own row */}
            {!!day.is_working && (
              <Box sx={{ display: { xs: "flex", sm: "none" }, alignItems: "center", gap: 1, mt: 1.5, pl: 1 }}>
                <TextField
                  variant="outlined"
                  placeholder="Open Time"
                  type="time"
                  value={day.start_time}
                  disabled={isSubmitting}
                  onChange={(e) => changeOpenTime(e.target.value, index)}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
                <Typography variant="body1">to</Typography>
                <TextField
                  variant="outlined"
                  placeholder="Close Time"
                  type="time"
                  value={day.end_time}
                  disabled={isSubmitting}
                  onChange={(e) => changeCloseTime(e.target.value, index)}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={showBarberScheduleDialog}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        {barberName} Schedule
        <IconButton
          //disabled={isLoading}
          aria-label="close"
          onClick={() =>
            setShowBarberScheduleDialog({ show: false, schedule: schedule })
          }
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {showAlert.show === true && (
          <Alert sx={{ mb: 2 }} severity={showAlert.type}>
            {showAlert.message}
          </Alert>
        )}

        {isLoading ? (
          <LoadingComponent marginTop={5} marginBottom={5} />
        ) : (
          schedule && (
            <Box
              sx={{
                backgroundColor: "offWhite",
                borderRadius: 2,
                border: `0.1px solid`,
                borderColor: "customDivider",
                width: "100%",
                overflow: "hidden",
                mt: 3,
              }}
            >
              {renderSchedule()}
            </Box>
          )
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: isLoading ? "none" : "flex",
          justifyContent: "flex-end",
          ml: 2,
          mr: 2,
        }}
      >
        {/* Right side */}
        <Box>
          <Button
            onClick={() =>
              setShowBarberScheduleDialog({ show: false, schedule: schedule })
            }
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => saveBarberSchedule()}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress
                enableTrackSlot
                size="25px"
                sx={{ color: "text.primary" }}
              />
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BarberScheduleDialog;
